"use client";

import React, { useState } from "react";
import { useTranslations } from "@/providers/TranslationsProvider";
import { Doctor } from "@/types/doctor";
import Button from "@/components/Button";
import DoctorAvatar from "@/components/DoctorAvatar";
import DoctorLicence from "@/components/DoctorLicence";
import { useLocalization } from "@/providers/useLocalization";
import { TOP_SPECIALTIES, termLabel } from "@/utils/profile";
interface DoctorCardProps {
  doctor: Doctor;
  onBook: (doctor: Doctor) => Promise<void>;
  priority?: boolean;
}
const CALL_TYPE_ICONS: Record<string, string> = {
  phone: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  voice: "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z",
  video: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
  text: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
};

/**
 * Compact, horizontal doctor card: identity and a one-line pitch up top,
 * specialties in a single row, and the call types next to the booking action.
 */
const DoctorCard: React.FC<DoctorCardProps> = ({
  doctor,
  onBook,
  priority = false
}) => {
  const {
    t
  } = useTranslations();
  const {
    language
  } = useLocalization();
  const [loading, setLoading] = useState(false);
  const profile = doctor.profile;
  const topSpecialties = profile?.specialties.slice(0, TOP_SPECIALTIES) ?? [];
  const summary = [profile?.headline, profile?.bio, doctor.bio].find((value): value is string => typeof value === "string" && value.trim().length > 0 && value.trim() !== "0");
  const specialty = typeof doctor.specialty === "string" ? doctor.specialty.trim() : "";
  const callTypes = doctor.callTypes ?? [];
  const hasDetails = Boolean(summary || profile?.yearsOfExperience || doctor.licence);

  // Call type names live under `callTypes`; `labels.*` is kept as a fallback for older bundles.
  const callTypeLabel = (type: string) => t(`callTypes.${type}`, t(`labels.${type}`, type.charAt(0).toUpperCase() + type.slice(1)));
  const handleBook = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await onBook(doctor);
    } finally {
      setLoading(false);
    }
  };
  return <article onClick={handleBook} className="group doctor-card-spring p-4 sm:p-5 rounded-3xl flex flex-col gap-3.5 cursor-pointer">
      <div className={`flex gap-3.5 ${hasDetails ? "items-start" : "items-center"}`}>
        <DoctorAvatar src={doctor.avatar} name={doctor.name} size={64} className="w-16 h-16" priority={priority} />

        <div className="flex-1 min-w-0">
          <h2 className="text-[17px] font-semibold text-[var(--foreground)] tracking-tight leading-snug truncate">
            {/* bdi: a name in another script keeps its own direction without leaving the avatar's side */}
            <bdi>{doctor.name}</bdi>
          </h2>
          {summary && <p className="mt-0.5 text-[13px] text-[var(--foreground-secondary)] leading-snug line-clamp-2">
              <bdi>{summary}</bdi>
            </p>}
          {profile?.yearsOfExperience ? <p className="mt-1 text-[12px] font-medium text-[var(--foreground-tertiary)]">
              {t("labels.yearsExperience", "{{count}} years experience").replace("{{count}}", String(profile.yearsOfExperience))}
            </p> : null}
          {doctor.licence && <DoctorLicence licence={doctor.licence} className="mt-1 text-[12px] font-medium text-[var(--foreground-secondary)]" />}
        </div>
      </div>

      {topSpecialties.length > 0 ? <ul className="flex flex-wrap gap-1.5 max-h-[1.625rem] overflow-hidden" aria-label={t("labels.specialties", "Specialties")}>
          {topSpecialties.map(term => <li key={term.uuid} className="max-w-full truncate px-2.5 py-1 rounded-full bg-[#0066CC]/[0.08] dark:bg-[#0A84FF]/15 text-[12px] font-medium leading-none text-[#0066CC] dark:text-[#409CFF]">
              {termLabel(term, language)}
            </li>)}
        </ul> : specialty && specialty !== "0" ? <p className="text-[13px] font-medium text-[#0066CC] dark:text-[#409CFF]">{specialty}</p> : null}

      <div className={`mt-auto flex items-center gap-3 ${callTypes.length > 0 ? "pt-3.5 border-t border-black/[0.06] dark:border-white/[0.08]" : ""}`}>
        {callTypes.length > 0 && <ul className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--foreground-secondary)]">
            {callTypes.map(type_0 => <li key={type_0} className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d={CALL_TYPE_ICONS[type_0] ?? CALL_TYPE_ICONS.text} />
                </svg>
                <span>{callTypeLabel(type_0)}</span>
              </li>)}
          </ul>}

        <Button onClick={handleBook} loading={loading} variant="theme" size="md" fullWidth={callTypes.length === 0} className="shrink-0 min-h-10 px-5! rounded-full shadow-none! text-[14px]!">
          {t("book appointment")}
        </Button>
      </div>
    </article>;
};
export default DoctorCard;